import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import {
  PaymentPageType,
  WithdrawMasterPayload,
  FolioDetail,
  WithdrawAccountDetail,
  OrderOTPTriggerPayload,
  OrderPayload,
  OrderOtpState
} from '../../types/mf-execution.types'
import {
  OTPVerificationState,
  OTPVerificationHandlers,
  OTPVerificationFailedCase as OTPFailedCase
} from '../../types/otp.types'
import { useMutation, useQuery } from '@tanstack/react-query'
import { fetchUserBankAccounts, createOrder, triggerOrderOTP, fetchTaxCalculations } from '../../repository/mfExecution'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { WithdrawContext } from '../contexts/WithdrawContext'
import { useMetaDataContext } from '../../state-management/contexts/MetaDataContext'
import { useMFExecutionContext } from '../../state-management/contexts/MFExecutionContext'
import { calculateTieredExitLoad, withdrawsToFolioLinkedBank } from '../../utils/mf-execution/withdrawUtils'
import { manualDelay } from '../../utils/utils'
import { OTP_VERIFICATION_DELAY } from '../../constants'
import {
  AmountAdjustmentReason,
  normalizeAllocationAmount,
} from '../../utils/mf-execution/paymentAmount'

interface WithdrawProviderProps {
  children: ReactNode
}

const OTP_VERIFICATION_LIMIT = 300

const WithdrawProvider: React.FC<WithdrawProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isin } = useMetaDataContext()
  const { mfExecutionData } = useMFExecutionContext()
  const [isWithdrawOTPVerified, setIsWithdrawOTPVerified] = useState(false)
  const [withdrawMasterPayload, setWithdrawMasterPayload] = useState<WithdrawMasterPayload>({
    accountId: '',
    transactionType: 'REDEMPTION',
    isin: '',
    amount: 0,
    folioNumber: '',
    isAllUnits: false
  })
  const [amount, setAmount] = useState(0)
  const [initialShownAmount, setInitialShownAmount] = useState<number | null>(null)
  const [fromAdvisory, setFromAdvisory] = useState(() => searchParams.get('fromAdvisory') === 'true')
  const [fromApp, setFromApp] = useState(() => searchParams.get('from') === 'APP')
  const [isFullWithdrawal, setIsFullWithdrawal] = useState(
    () => searchParams.get('isFullWithdrawal') === 'true'
  )
  const [amountAdjustmentReason, setAmountAdjustmentReason] = useState(AmountAdjustmentReason.NONE)
  const [selectedFolio, setSelectedFolio] = useState<FolioDetail | null>(null)
  const [selectedAccount, setSelectedAccount] = useState<WithdrawAccountDetail | null>(null)
  const [otpVerificationCount, setOtpVerificationCount] = useState(0)
  const [otpReferenceId, setOtpReferenceId] = useState('')
  const [calculatedExitLoad, setCalculatedExitLoad] = useState(0)
  const [isOTPVerificationLimitExceeded, setIsOTPVerificationLimitExceeded] = useState(false)
  const [OTPVerificationFailedCase, setOTPVerificationFailedCase] = useState<OTPFailedCase | null>(null)
  const [otpState, setOtpState] = useState<OrderOtpState | null>(null)

  // Opening amount only. Withdraw all and keypad edits leave this as-is.
  const rememberInitialShownAmount = (shownAmount: number) => {
    if (shownAmount <= 0) return
    setInitialShownAmount((current) => current ?? shownAmount)
  }

  const handleAmountChange = (amountParam: number) => {
    setAmount(amountParam)
  }

  const clearAmountAdjustmentReason = useCallback(() => {
    setAmountAdjustmentReason(AmountAdjustmentReason.NONE)
  }, [])

  const handleFolioSelection = (folio: FolioDetail | null) => {
    setSelectedFolio(folio)
    const query = searchParams.toString()
    const path = `/mutual-fund/${isin}/payment/${PaymentPageType.WITHDRAW}${query ? `?${query}` : ''}`
    if (searchParams?.get('from') === 'APP') {
      navigate(path)
    } else {
      navigate(path, { replace: true })
    }
  }

  useEffect(() => {
    if (selectedFolio === null) {
      setSelectedFolio(mfExecutionData?.sell?.folioDetails?.[0] ?? null)
    }
  }, [mfExecutionData?.sell?.folioDetails])

  // Apply entry amount only on payment (not folio-selection) so rules match the chosen folio.
  useEffect(() => {
    const amountFromParams = parseFloat(searchParams.get('amount') ?? '0')
    const amountFromEntry =
      Number.isFinite(amountFromParams) && amountFromParams > 0 ? amountFromParams : 0
    const fromAdvisoryEntry = searchParams.get('fromAdvisory') === 'true'
    const fromAppEntry = searchParams.get('from') === 'APP'
    const isFullWithdrawalEntry = searchParams.get('isFullWithdrawal') === 'true'
    setFromAdvisory(fromAdvisoryEntry)
    setFromApp(fromAppEntry)
    if (isFullWithdrawalEntry) {
      setIsFullWithdrawal(true)
    }

    if (location.pathname.includes('folio-selection')) return
    if (!selectedFolio) return

    if (isFullWithdrawalEntry) {
      const fullFolioAmount = Math.floor(Math.abs(selectedFolio?.folioAmount ?? 0))
      setAmount(fullFolioAmount)
      rememberInitialShownAmount(fullFolioAmount)
      setAmountAdjustmentReason(AmountAdjustmentReason.NONE)
      if (searchParams.has('amount') || searchParams.has('isFullWithdrawal')) {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('amount')
        nextParams.delete('isFullWithdrawal')
        setSearchParams(nextParams, { replace: true })
      }
      return
    }

    if (amountFromEntry <= 0) return

    const folioRules = mfExecutionData?.sell?.folioDetails?.find(
      (folio) => folio?.folioNumber === selectedFolio?.folioNumber
    )
    if (fromAdvisoryEntry && !folioRules) return

    if (fromAdvisoryEntry) {
      const { amount: normalizedAmount, adjustment } = normalizeAllocationAmount(amountFromEntry, {
        minAmount: folioRules?.minAmount ?? 0,
        maxAmount: folioRules?.folioAmount ?? 0,
        stepper: folioRules?.stepper,
      })
      setAmount(normalizedAmount)
      rememberInitialShownAmount(normalizedAmount)
      setAmountAdjustmentReason(adjustment)
    } else {
      setAmount(amountFromEntry)
      rememberInitialShownAmount(amountFromEntry)
      setAmountAdjustmentReason(AmountAdjustmentReason.NONE)
    }

    if (searchParams.has('amount')) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('amount')
      setSearchParams(nextParams, { replace: true })
    }
  }, [selectedFolio, mfExecutionData?.sell?.folioDetails, location.pathname])

  const handleAccountSelection = (account: WithdrawAccountDetail | null) => {
    setSelectedAccount(account)
  }

  const {
    data: bankAccounts,
    isSuccess: isBankAccountsQuerySuccess,
    isFetchedAfterMount: isBankAccountsFetchedAfterMount,
    refetch: refetchBankAccounts
  } = useQuery({
    queryKey: ['withdraw-bank-accounts', selectedFolio?.folioNumber],
    queryFn: () => fetchUserBankAccounts(selectedFolio?.folioNumber ?? ''),
    enabled: !!selectedFolio?.folioNumber
  })
  const bankAccountList = bankAccounts?.folioAccountDetailsList ?? []
  const withdrawWithoutBankAccount = withdrawsToFolioLinkedBank(
    selectedFolio,
    isBankAccountsQuerySuccess,
    isBankAccountsFetchedAfterMount,
    bankAccountList
  )

  useEffect(() => {
    if (selectedFolio?.folioNumber) {
      refetchBankAccounts()
    }
  }, [selectedFolio?.folioNumber])

  useEffect(() => {
    if (bankAccounts) {
      setSelectedAccount(bankAccounts?.folioAccountDetailsList?.[0] ?? null)
    }
  }, [bankAccounts])

  const {
    data: taxCalculations,
    isLoading: isTaxCalculationsLoading,
    isError: isTaxCalculationsError,
    refetch: refetchTaxCalculations
  } = useQuery({
    queryKey: ['tax-calculations', selectedFolio?.folioNumber, isin],
    queryFn: () => fetchTaxCalculations({ folioNumber: selectedFolio?.folioNumber ?? '', isin: isin, amount: amount }),
    enabled: !!selectedFolio?.folioNumber && !!isin
  })

  // Recalculate exit load when taxCalculations change
  useEffect(() => {
    if (amount > 0 && taxCalculations?.exitLoadSlabs) {
      const exitLoad = calculateTieredExitLoad(amount, taxCalculations.exitLoadSlabs)
      setCalculatedExitLoad(exitLoad)
    }
  }, [taxCalculations?.exitLoadSlabs, amount])

  const { mutate: triggerOTPMutate } = useMutation({
    mutationFn: (payload: OrderOTPTriggerPayload) => triggerOrderOTP(payload),
    onSuccess: (data) => {
      console.log('OTP triggered successfully', data)
      setOtpReferenceId(data?.otpReferenceId)
    },
    onError: (error) => {
      console.error('OTP trigger error:', error)
    }
  })

  // This same API is used for OTP Verification and Order Creation
  const { mutate: createOrderMutate, isPending: isOTPVerificationLoading } = useMutation({
    mutationFn: (payload: OrderPayload) => createOrder(payload),
    onSuccess: async (data) => {
      setOtpState(data?.otpState ?? OrderOtpState.UNKNOWN)
      if (data?.isOtpVerified) {
        setIsWithdrawOTPVerified(true)
        await manualDelay(OTP_VERIFICATION_DELAY)
        // Navigate to withdrawal success page
        navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.WITHDRAW}/success`)
      } else {
        // Handle different OTP states
        if (data?.otpState === OrderOtpState.MAXIMUM_LIMIT_REACHED) {
          setIsOTPVerificationLimitExceeded(true)
        } else if (data?.otpState === OrderOtpState.INVALID_OTP) {
          setOTPVerificationFailedCase(OTPFailedCase.INVALID_OTP)
        } else if (data?.otpState === OrderOtpState.MAXIMUM_INCORRECT_RESPONSE) {
          setIsOTPVerificationLimitExceeded(true)
        } else {
          setOTPVerificationFailedCase(OTPFailedCase.OTP_VERIFICATION_FAILED)
        }
      }
    },
    onError: () => {
      setOtpState(OrderOtpState.UNKNOWN)
      // Navigate to withdrawal failed page on error
      navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.WITHDRAW}/failed`)
    }
  })

  const convertToOrderOTPTriggerPayload = (payload: WithdrawMasterPayload): OrderOTPTriggerPayload => {
    return {
      paymentMethodId: withdrawWithoutBankAccount ? null : payload.accountId,
      externalSell: withdrawWithoutBankAccount,
      transactionType: 'REDEMPTION',
      isin: payload.isin,
      amount: payload.amount,
      folioNumber: payload.folioNumber,
      isAllUnits: payload.isAllUnits
    } as OrderOTPTriggerPayload
  }

  const resetOTPVerificationState = () => {
    setIsOTPVerificationLimitExceeded(false)
    setOTPVerificationFailedCase(null)
    setOtpVerificationCount(0)
    setIsWithdrawOTPVerified(false)
    setOtpState(null)
  }

  const handleOTPTrigger = (payload: WithdrawMasterPayload) => {
    resetOTPVerificationState()
    setWithdrawMasterPayload(payload)
    triggerOTPMutate(convertToOrderOTPTriggerPayload(payload))
  }

  const handleWithdrawMasterPayload = (payload: WithdrawMasterPayload) => {
    setWithdrawMasterPayload(payload)
  }

  const isOTPValid = (otp: string) => {
    return otp.length === 6 && /^\d{6}$/.test(otp)
  }

  const handleWithdrawOTPVerification = async (otp: string) => {
    if (!isOTPValid(otp)) {
      setOTPVerificationFailedCase(OTPFailedCase.INVALID_OTP)
      return
    }
    if (otpVerificationCount >= OTP_VERIFICATION_LIMIT) {
      setIsOTPVerificationLimitExceeded(true)
      return
    }
    setOTPVerificationFailedCase(null)
    setOtpVerificationCount(otpVerificationCount + 1)
    createOrderMutate({
      otp: otp,
      otpReferenceId: otpReferenceId,
      orderDetails: convertToOrderOTPTriggerPayload(withdrawMasterPayload) as OrderOTPTriggerPayload
    })
  }

  const handleWithdrawResendOTP = () => {
    triggerOTPMutate(convertToOrderOTPTriggerPayload(withdrawMasterPayload) as OrderOTPTriggerPayload)
  }

  const otpVerificationState: OTPVerificationState = {
    isOTPVerified: isWithdrawOTPVerified,
    isOTPVerificationLoading,
    isOTPVerificationLimitExceeded,
    OTPVerificationFailedCase
  }

  const otpVerificationHandlers: OTPVerificationHandlers = {
    handleOTPVerification: handleWithdrawOTPVerification,
    handleResendOTP: handleWithdrawResendOTP
  }

  return (
    <WithdrawContext.Provider
      value={{
        withdrawMasterPayload,
        handleWithdrawMasterPayload,
        isWithdrawOTPVerified,
        handleWithdrawOTPVerification,
        otpVerificationState,
        otpVerificationHandlers,
        // Folio selection state
        selectedFolio,
        handleFolioSelection,
        // Account selection state
        selectedAccount,
        handleAccountSelection,
        // Amount state
        amount,
        initialShownAmount,
        handleAmountChange,
        fromAdvisory,
        fromApp,
        isFullWithdrawal,
        amountAdjustmentReason,
        clearAmountAdjustmentReason,
        // Bank accounts
        bankAccounts: bankAccountList,
        isBankAccountsFetchedAfterMount,
        withdrawsToFolioLinkedBank: withdrawWithoutBankAccount,
        handleOTPTrigger,
        taxCalculations: taxCalculations ?? null,
        isTaxCalculationsLoading: isTaxCalculationsLoading,
        isTaxCalculationsError: isTaxCalculationsError,
        calculatedExitLoad,
        handleRefetchTaxCalculations: refetchTaxCalculations,
        otpState
      }}
    >
      {children}
    </WithdrawContext.Provider>
  )
}

export default WithdrawProvider
